
/* ==========================================================================
   SFF VECTOR & TENSOR SCORING ENGINE (/database/vector-engine.js)
   ========================================================================== */

/**
 * Normalizes a numeric scalar between 0.0 and 1.0 using Min-Max scaling.
 */
export function normalize(val, min, max) {
  if (max === min) return 0;
  const clamped = Math.max(min, Math.min(max, val));
  return (clamped - min) / (max - min);
}

/**
 * Computes the Cosine Similarity between two Float32 vectors.
 * Returns a value between -1.0 and 1.0 (1.0 = identical direction).
 */
export function cosineSimilarity(vecA, vecB) {
  let dotProduct = 0.0;
  let normA = 0.0;
  let normB = 0.0;

  for (let i = 0; i < vecA.length; i++) {
    dotProduct += vecA[i] * vecB[i];
    normA += vecA[i] * vecA[i];
    normB += vecB[i] * vecB[i];
  }

  if (normA === 0 || normB === 0) return 0;
  return dotProduct / (Math.sqrt(normA) * Math.sqrt(normB));
}

/**
 * Calculates weighted Euclidean Distance Proximity score (0.0 to 1.0).
 */
export function weightedProximity(targetVec, candidateVec, weights) {
  let weightedSum = 0.0;
  let maxWeightSum = 0.0;

  for (let i = 0; i < targetVec.length; i++) {
    const diff = targetVec[i] - candidateVec[i];
    const w = weights[i] || 1.0;
    weightedSum += w * (diff * diff);
    maxWeightSum += w;
  }

  const distance = Math.sqrt(weightedSum / maxWeightSum);
  return Math.max(0.0, 1.0 - distance);
}

/**
 * Converts active Case Spatial Constraints into a Target Vector.
 * Tensor Vector Map: [MaxCPU, MaxGPU_L, MaxGPU_T, MaxAIO, Volume]
 */
export function buildCaseTargetVector(caseObj, activeSpine = null) {
  const maxCpu = (caseObj.hasSpine && activeSpine && typeof activeSpine.maxCPU === 'number')
    ? activeSpine.maxCPU
    : (caseObj._maxCPU || 0);

  const maxGpuT = (caseObj.hasSpine && activeSpine && typeof activeSpine.maxThick === 'number')
    ? activeSpine.maxThick
    : (caseObj._maxThick || 0);

  const tensor = new Float32Array(5);
  tensor[0] = normalize(maxCpu, 30, 180);       // CPU Clearance
  tensor[1] = normalize(caseObj._maxGPU, 150, 400); // GPU Length
  tensor[2] = normalize(maxGpuT, 20, 90);       // GPU Thickness
  tensor[3] = normalize(caseObj._maxAIO, 0, 360);  // Max AIO Size
  tensor[4] = normalize(caseObj.volume, 3, 35);   // Volume
  return tensor;
}

/**
 * Encodes an Air Cooler candidate into a Feature Vector.
 * Vector Map: [HeightFillRatio, SFFBrandHeritage, ThermalMassTier]
 */
export function buildAirCoolerVector(title, height, maxCpu) {
  const tensor = new Float32Array(3);
  const n = title.toLowerCase();

  // 1. Height Fill Ratio (Maximizes thermal dissipation within clearance)
  tensor[0] = height / maxCpu;

  // 2. SFF Benchmark/Heritage Rating
  const topModels = [
    'phantom spirit', 'peerless assassin', 'silver soul', 'frost tower', 'frost spirit', 
    'axp90', 'axp120', 'axp-90', 'nh-d15', 'nh-u12a', 'nh-d12l', 'nh-u9s', 'nh-l12s', 
    'nh-l9', 'nh-l9i', 'nh-l9a', 'ak620', 'ak400', 'an600', 'fuma 3', 'mugen 5', 
    'big shuriken', 'is-55', 'is-67', 'black ridge'
  ];
  tensor[1] = topModels.some(m => n.includes(m)) ? 1.0 : 0.4;

  // 3. Brand Prestige Score
  const topBrands = ['thermalright', 'noctua', 'deepcool', 'scythe', 'id-cooling'];
  tensor[2] = topBrands.some(b => n.includes(b)) ? 0.9 : 0.5;

  return tensor;
}

/**
 * Encodes an AIO Liquid Cooler into a Feature Vector.
 * Vector Map: [RadiatorSizeRatio, SFFSuitability, CompactPumpScore, StackEfficiency]
 */
export function buildAioVector(title, radSize, maxAio, radThick, pumpHeight) {
  const tensor = new Float32Array(4);
  const n = title.toLowerCase();

  // 1. Radiator Size Utilization Ratio
  tensor[0] = radSize / (maxAio || 1);

  // 2. Curated SFF Model Suitability
  const sffModels = [
    'atmos', 'frozen prism', 'frozen edge', 'aqua elite', 'frozen horizon', 'frozen magic',
    'galahad', 'ga ii', 'trinity', 'ls520', 'lt520', 'ls720', 'lt720', 
    'vida', 'icegem', 'eisbaer', 'dashflow', 'space', 'glacier', 'core'
  ];
  tensor[1] = sffModels.some(m => n.includes(m)) ? 1.0 : 0.3;

  // 3. Pump Block Compactness Score (Lower height = higher score)
  tensor[2] = normalize(75 - pumpHeight, 0, 35);

  // 4. Radiator Thickness Score (Standard <= 27mm preferred)
  tensor[3] = radThick <= 27 ? 1.0 : 0.4;

  return tensor;
}

/**
 * Encodes a GPU candidate into a Performance & Dimension Feature Vector.
 * Vector Map: [PerformanceTier, LengthUtilization, ThicknessUtilization]
 */
export function buildGpuVector(title, len, thick, maxGpuL, maxGpuT) {
  const tensor = new Float32Array(3);
  const n = title.toLowerCase();

  // 1. Current-Gen Performance Tier (RTX 50 Series & RX 9000 Series)
  let tierScore = 0.0;
  if (n.includes('5090')) tierScore = 1.0;
  else if (n.includes('5080') || n.includes('9070 xt')) tierScore = 0.9;
  else if (n.includes('5070 ti') || n.includes('9070')) tierScore = 0.82;
  else if (n.includes('5070') || n.includes('9070 gre')) tierScore = 0.75;
  else if (n.includes('5060 ti') || n.includes('9060 xt')) tierScore = 0.68;
  else if (n.includes('5060') || n.includes('9060')) tierScore = 0.6;
  tensor[0] = tierScore;

  // 2. Length Utilization Ratio
  tensor[1] = len / maxGpuL;

  // 3. Thickness Fit Efficiency
  tensor[2] = maxGpuT > 0 ? (thick / maxGpuT) : 0.5;

  return tensor;
}
